import React, { useState } from "react";
import { compose } from "recompose";
import { FormattedMessage, defineMessages, injectIntl } from "react-intl";
import EnhanceRegisterForm from "./EnhanceRegisterForm";
import LoginMutation from "theme/modules/User/LoginForm/LoginMutation.gql";
import RegisterMutation from "./RegisterMutation.gql";
import Form from "theme/components/atoms/Form/Form";
import Fieldset from "theme/components/atoms/Form/Fieldset";
import FormItem from "theme/components/molecules/Form/Item";
import FormActions from "theme/components/molecules/Form/FormActions";
import { Text, Password } from "theme/components/atoms/Form/Input";
import TitleSelect from "theme/components/atoms/Form/Input/TitleSelect";
import SubmitButton from "theme/components/atoms/Button/SubmitButton";
import { ErrorAlert } from "theme/components/molecules/Alert";
import config from "config/website";

const messages = defineMessages({
  lastnameLabel: {
    id: "modules.User.RegisterForm.lastname",
    defaultMessage: "Lastname",
  },
  lastnameError: {
    id: "modules.User.RegisterForm.lastnameError",
    defaultMessage: "Please fill in your lastname",
  },
  firstnameLabel: {
    id: "modules.User.RegisterForm.firstname",
    defaultMessage: "Firstname",
  },
  firstnameError: {
    id: "modules.User.RegisterForm.firstnameError",
    defaultMessage: "Please fill in your firstname",
  },
  emailLabel: {
    id: "modules.User.RegisterForm.email",
    defaultMessage: "Email",
  },
  emailError: {
    id: "modules.User.RegisterForm.emailError",
    defaultMessage: "Enter a valid email address",
  },
  passwordLabel: {
    id: "modules.User.RegisterForm.password",
    defaultMessage: "Password",
  },
  passwordConfirmationLabel: {
    id: "modules.User.RegisterForm.passwordConfirm",
    defaultMessage: "Password confirmation",
  },
  passwordMessage: {
    id: "modules.User.RegisterForm.passwordMessage",
    defaultMessage:
      "Your password must contain at least 5 characters and 1 number",
  },
  passwordNotMatchingError: {
    id: "modules.User.RegisterForm.passwordNotMatchingError",
    defaultMessage: "Passwords do not match",
  },
  titleError: {
    id: "modules.User.RegisterForm.titleError",
    defaultMessage: "Please choose your title",
  },
  titleLabel: {
    id: "modules.User.RegisterForm.title",
    defaultMessage: "Title",
  },
  newsletterCheckbox: {
    id: "modules.User.RegisterForm.newsletter",
    defaultMessage:
      "Inscrivez-vous à la Newsletter Collégien pour recevoir nos emails concernant nos offres. Vous pourrez vous désabonner à tout moment .",
  },
});

const RegisterForm = (props) => {
  const [formIsValid, setFormIsValid] = useState(false);
  const [isSubscribedToNewsletter, setIsSubscribedToNewsletter] =
    useState(false);

  const validateEmail = (value) => {
    const emailRegex = /@.*\.[a-z0-9]{0,3}$/;
    return !!String(value.email).toLowerCase().match(emailRegex);
  };

  const handleCheckboxChange = (e) => {
    setIsSubscribedToNewsletter(e.target.checked);
  };

  return (
    <Form
      onValid={() => setFormIsValid(true)}
      onInvalid={() => setFormIsValid(false)}
      onValidSubmit={(data) => {
        data.is_subscribed_to_newsletter = isSubscribedToNewsletter;
        props.setPending(true);
        props.register(data);
      }}
    >
      <Fieldset large>
        {props.errorMessage && <ErrorAlert>{props.errorMessage}</ErrorAlert>}
        {config.useUserTitles && (
          <FormItem label={props.intl.formatMessage(messages.titleLabel)}>
            <TitleSelect name="title" id="title" />
          </FormItem>
        )}
        <FormItem label={props.intl.formatMessage(messages.lastnameLabel)}>
          <Text
            name="lastname"
            id="lastname"
            required
            validations={{
              minLength: 1,
            }}
            validationError={props.intl.formatMessage(messages.lastnameError)}
          />
        </FormItem>
        <FormItem label={props.intl.formatMessage(messages.firstnameLabel)}>
          <Text
            name="firstname"
            id="firstname"
            required
            validations={{
              minLength: 1,
            }}
            validationError={props.intl.formatMessage(messages.firstnameError)}
          />
        </FormItem>
        <FormItem label={props.intl.formatMessage(messages.emailLabel)}>
          <Text
            name="email"
            id="email"
            validations={{
              validateEmail,
            }}
            validationError={props.intl.formatMessage(messages.emailError)}
            required
          />
        </FormItem>
        <FormItem label={props.intl.formatMessage(messages.passwordLabel)}>
          <Password
            name="password"
            id="password"
            required
            validationError={
              <FormattedMessage
                id="modules.User.RegisterForm.passwordMessage"
                defaultMessage="Your password must contain at least 5 characters and 1 number"
                values={{ minLength: 8 }}
              />
            }
            validations={{
              minLength: 8,
            }}
          />
        </FormItem>
        <FormItem
          label={props.intl.formatMessage(messages.passwordConfirmationLabel)}
        >
          <Password
            name="password_confirmation"
            id="password_confirmation"
            validations="equalsField:password"
            required
            validationError={props.intl.formatMessage(
              messages.passwordNotMatchingError
            )}
          />
        </FormItem>
        <div className="newsletter-checkbox">
          <FormItem
            label={props.intl.formatMessage(messages.newsletterCheckbox)}
          >
            <input
              type="checkbox"
              name="is_subscribed_to_newsletter"
              id="is_subscribed_to_newsletter"
              value="1"
              checked={isSubscribedToNewsletter}
              onChange={handleCheckboxChange}
            />
          </FormItem>
        </div>
        <FormActions appearance="full">
          <SubmitButton
            type="submit"
            appearance="primary"
            disabled={!formIsValid}
          >
            <FormattedMessage
              id="modules.RegisterForm.create"
              defaultMessage="Create my account"
            />
          </SubmitButton>
        </FormActions>
      </Fieldset>
    </Form>
  );
};

export default compose(
  EnhanceRegisterForm({ LoginMutation, RegisterMutation }),
  injectIntl
)(RegisterForm);
